import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Play, Bell, AlertCircle, ExternalLink, Sparkles, X, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { contactService, schedulerService } from '../services/api';
import GenerateMessageModal from '../componets/GenerateMessageModal';


const Scheduler = () => {
  const [contacts, setContacts] = useState([]);
  const [schedulers, setSchedulers] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTier, setUserTier] = useState('free');
  const [limit, setLimit] = useState(2);
  const [activeCount, setActiveCount] = useState(0);

  const [checkingScheduler, setCheckingScheduler] = useState(null);

  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  
  const [expandedSchedulers, setExpandedSchedulers] = useState(new Set());
  
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalStep, setModalStep] = useState(1); // 1: select contact, 2: configure
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState('weekly');
  const [selectedAttributes, setSelectedAttributes] = useState([
    'news',
    'company_updates',
    'achievements'
  ]);
  const [adding, setAdding] = useState(false);

  const AVAILABLE_ATTRIBUTES = [
    { id: 'news', label: 'Recent News', description: 'Personal news and mentions' },
    { id: 'company_updates', label: 'Company Updates', description: 'Company news and changes' },
    { id: 'achievements', label: 'Achievements', description: 'Awards, promotions, milestones' },
    { id: 'industry_news', label: 'Industry News', description: 'Relevant industry developments' },
    { id: 'interests', label: 'Personal Interests', description: 'Topics they care about' },
    { id: 'role_changes', label: 'Role Changes', description: 'Job changes and promotions' },
  ];

  const styles = {
  page: {
    padding: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '2rem',
    fontFamily: "'Syne', sans-serif",
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: '0.5rem',
  },
  subtitle: {
    color: '#475569',
    fontSize: '1rem',
    marginBottom: '1rem',
  },
  limitBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    fontSize: '0.9rem',
    color: '#1e40af',
    fontWeight: 600,
  },
  limitBadgeWarning: {
    background: '#fef3c7',
    border: '1px solid #fde047',
    color: '#854d0e',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.875rem 1.5rem',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 200ms',
    fontFamily: "'Syne', sans-serif",
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    marginBottom: '2rem',
  },
  section: {
    marginBottom: '2rem',
  },
  sectionTitle: {
    fontSize: '1.3rem',
    fontFamily: "'Syne', sans-serif",
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '1rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '1.5rem',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '1.5rem',
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
    border: '1px solid #e2e8f0',
    position: 'relative',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: '1rem',
  },
  contactName: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '0.25rem',
  },
  contactCompany: {
    fontSize: '0.9rem',
    color: '#64748b',
  },
  frequencyBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    background: '#f1f5f9',
    color: '#475569',
  },
  cardInfo: {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '1rem',
  },
  cardActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 200ms',
    background: '#ffffff',
    color: '#475569',
    flex: 1,
  },
  deleteButton: {
    background: '#fef2f2',
    border: '2px solid #fecaca',
    color: '#dc2626',
  },
  findingCard: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 2px 4px rgb(0 0 0 / 0.05)',
    border: '1px solid #e2e8f0',
    marginBottom: '1rem',
  },
  findingUnread: {
    borderLeft: '4px solid #3b82f6',
    background: '#eff6ff',
  },
  findingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'start',
    marginBottom: '0.75rem',
  },
  findingTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '0.25rem',
  },
  findingMeta: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginBottom: '0.5rem',
  },
  findingDescription: {
    fontSize: '0.9rem',
    color: '#475569',
    lineHeight: 1.6,
    marginBottom: '0.75rem',
  },
  findingActions: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  linkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.4rem 0.75rem',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 200ms',
    background: '#f1f5f9',
    color: '#475569',
    textDecoration: 'none',
  },
  generateButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '2rem',
  },
  modalContent: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '2rem',
    maxWidth: '900px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '2px solid #e2e8f0',
  },
  modalTitle: {
    fontSize: '1.5rem',
    fontFamily: "'Syne', sans-serif",
    fontWeight: 700,
    color: '#0f172a',
  },
  modalSubtitle: {
    fontSize: '0.9rem',
    color: '#64748b',
    marginTop: '0.25rem',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '8px',
    transition: 'all 200ms',
    color: '#64748b',
  },
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  selectableCard: {
    background: '#ffffff',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    padding: '1.25rem',
    cursor: 'pointer',
    transition: 'all 200ms',
    position: 'relative',
  },
  selectableCardSelected: {
    borderColor: '#667eea',
    background: '#f0f4ff',
  },
  cardCheckbox: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    border: '2px solid #e2e8f0',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 200ms',
  },
  cardCheckboxSelected: {
    background: '#667eea',
    borderColor: '#667eea',
  },
  miniAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '1.1rem',
    color: 'white',
    marginBottom: '0.75rem',
  },
  cardName: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '0.25rem',
  },
  cardCompany: {
    fontSize: '0.85rem',
    color: '#64748b',
  },
  configSection: {
    marginBottom: '2rem',
  },
  sectionLabel: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '1rem',
    display: 'block',
  },
  frequencyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '0.75rem',
    marginBottom: '2rem',
  },
  frequencyOption: {
    padding: '1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 200ms',
    background: '#ffffff',
  },
  frequencyOptionSelected: {
    borderColor: '#667eea',
    background: '#f0f4ff',
  },
  frequencyLabel: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '0.25rem',
  },
  frequencyDesc: {
    fontSize: '0.75rem',
    color: '#64748b',
  },
  attributeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '0.75rem',
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  attributeOption: {
    padding: '1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 200ms',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'start',
    gap: '0.75rem',
  },
  attributeOptionSelected: {
    borderColor: '#667eea',
    background: '#f0f4ff',
  },
  attributeCheckbox: {
    width: '20px',
    height: '20px',
    borderRadius: '6px',
    border: '2px solid #e2e8f0',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '2px',
  },
  attributeCheckboxSelected: {
    background: '#667eea',
    borderColor: '#667eea',
  },
  attributeInfo: {
    flex: 1,
  },
  attributeLabel: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: '0.25rem',
  },
  attributeDesc: {
    fontSize: '0.75rem',
    color: '#64748b',
    lineHeight: 1.4,
  },
  modalActions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
    paddingTop: '1.5rem',
    borderTop: '2px solid #e2e8f0',
  },
  button: {
    padding: '0.75rem 1.5rem',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 200ms',
    fontFamily: "'Syne', sans-serif",
  },
  backButton: {
    background: '#f1f5f9',
    color: '#475569',
  },
  cancelButton: {
    background: '#f1f5f9',
    color: '#475569',
  },
  nextButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  submitButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem 2rem',
    color: '#64748b',
  },
  emptyIcon: {
    marginBottom: '1rem',
    color: '#cbd5e1',
  },
  emptyTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#475569',
    marginBottom: '0.5rem',
  },
};

useEffect(() => {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    @keyframes pulse-glow {
      0%, 100% {
        box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
      }
      50% {
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0);
      }
    }
    
    @keyframes bell-ring {
      0%, 100% { transform: rotate(0deg); }
      10%, 30%, 50%, 70%, 90% { transform: rotate(-10deg); }
      20%, 40%, 60%, 80% { transform: rotate(10deg); }
    }
    
    .unread-badge {
      animation: pulse-glow 2s ease-in-out infinite;
    }
    
    .bell-icon {
      display: inline-block;
      animation: bell-ring 2s ease-in-out infinite;
      transform-origin: top center;
    }
  `;
  document.head.appendChild(styleSheet);
  
  return () => {
    document.head.removeChild(styleSheet);
  };
}, []);


  useEffect(() => {
    loadData();
  }, []);
  

  const loadData = async () => {
    try {
      setLoading(true);
      const [contactsRes, schedulersRes, findingsRes] = await Promise.all([
        contactService.getContacts(),
        schedulerService.getSchedulers(),
        schedulerService.getFindings()
      ]);
      
      setContacts(contactsRes.contacts);
      setSchedulers(schedulersRes.schedulers);
      setFindings(findingsRes.findings);
      setUserTier(schedulersRes.user_tier);
      setLimit(schedulersRes.limit);
      setActiveCount(schedulersRes.active_count);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddScheduler = async () => {
    if (!selectedContact) return;
    
    setAdding(true);
    try {
      await schedulerService.createScheduler(
        selectedContact.id,
        selectedFrequency,
        selectedAttributes
      );
      
      resetModal();
      loadData();
    } catch (error) {
      console.error('Failed to add scheduler:', error);
      alert(error.response?.data?.error || 'Failed to add scheduler');
    } finally {
      setAdding(false);
    }
  };

  const resetModal = () => {
    setShowAddModal(false);
    setModalStep(1);
    setSelectedContact(null);
    setSelectedFrequency('weekly');
    setSelectedAttributes(['news', 'company_updates', 'achievements']);
  };

  const handleDeleteScheduler = async (schedulerId) => {
    if (!window.confirm('Are you sure you want to delete this scheduler?')) return;
    
    try {
      await schedulerService.deleteScheduler(schedulerId);
      loadData();
    } catch (error) {
      console.error('Failed to delete scheduler:', error);
    }
  };

  const handleCheckNow = async (schedulerId) => {
    setCheckingScheduler(schedulerId);
    try {
      const data = await schedulerService.checkNow(schedulerId);
      alert(`Check completed! Found ${data.findings_count} new items.`);
      loadData();
    } catch (error) {
      console.error('Failed to check now:', error);
      alert('Failed to perform check');
    } finally {
      setCheckingScheduler(null);
    }
  };

  const handleMarkRead = async (findingId) => {
    try {
      await schedulerService.markFindingRead(findingId);
      loadData();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const toggleAttribute = (attributeId) => {
    setSelectedAttributes(prev =>
      prev.includes(attributeId)
        ? prev.filter(id => id !== attributeId)
        : [...prev, attributeId]
    );
  };

  const toggleScheduler = (schedulerId) => {
    setExpandedSchedulers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(schedulerId)) {
        newSet.delete(schedulerId);
      } else {
        newSet.add(schedulerId);
      }
      return newSet;
    });
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const availableContacts = contacts.filter(contact => 
    !schedulers.find(s => s.contact_id === contact.id)
  );

  const canAddMore = activeCount < limit;

  if (loading) {
    return (
      <div style={{ ...styles.page, textAlign: 'center', padding: '4rem 2rem' }}>
        Loading schedulers...
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>AI Scheduler</h1>
        <p style={styles.subtitle}>
          Automatically monitor contacts for outreach opportunities
        </p>
        <div style={{
          ...styles.limitBadge,
          ...(activeCount >= limit ? styles.limitBadgeWarning : {})
        }}>
          <Calendar size={16} />
          {activeCount} / {limit} Active Schedulers
          {userTier === 'free' && ' (Upgrade for 5)'}
        </div>
      </div>

      <>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={!canAddMore || availableContacts.length === 0}
          style={{
            ...styles.addButton,
            opacity: !canAddMore || availableContacts.length === 0 ? 0.5 : 1,
            cursor: !canAddMore || availableContacts.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          <Plus size={20} />
          Add Scheduler
        </button>

        {!canAddMore && (
          <div style={{
            padding: '1rem',
            background: '#fef3c7',
            border: '1px solid #fde047',
            borderRadius: '12px',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <AlertCircle size={20} color="#854d0e" />
            <span style={{ color: '#854d0e', fontSize: '0.9rem' }}>
              You've reached your limit. Upgrade to premium for 5 active schedulers!
            </span>
          </div>
        )}
      </>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Active Schedulers</h2>
        {schedulers.length === 0 ? (
          <div style={styles.empty}>
            <Calendar size={48} style={styles.emptyIcon} />
            <div style={styles.emptyTitle}>No schedulers yet</div>
            <p>Add a scheduler to automatically monitor contacts for outreach opportunities</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {schedulers.map(scheduler => {
              const schedulerFindings = findings.filter(f => f.contact_id === scheduler.contact_id);
              const hasFindings = schedulerFindings.length > 0;
              const isExpanded = expandedSchedulers.has(scheduler.id);
              
              return (
                <motion.div
                  key={scheduler.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  {/* Scheduler Header - Always Visible & Clickable */}
                  <div 
                    style={{
                      padding: '1.5rem',
                      cursor: 'pointer',
                      transition: 'background 200ms',
                    }}
                    onClick={() => toggleScheduler(scheduler.id)}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                          color: '#64748b',
                          flexShrink: 0,
                        }}>
                          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <div style={styles.contactName}>{scheduler.contact_name}</div>
                          <div style={styles.contactCompany}>{scheduler.contact_company}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        {scheduler.unread_findings > 0 && (
                          <div 
                            className="unread-badge"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.35rem 0.75rem',
                              background: '#eff6ff',
                              border: '1px solid #bfdbfe',
                              borderRadius: '6px',
                              color: '#1e40af',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                            }}
                          >
                            <span className="bell-icon">
                              <Bell size={14} />
                            </span>
                            {scheduler.unread_findings} new
                          </div>
                        )}
                        
                        <div style={styles.frequencyBadge}>
                          {scheduler.frequency}
                        </div>
                      </div>
                    </div>

                    {scheduler.last_check && (
                      <div style={{
                        marginTop: '0.75rem',
                        marginLeft: '40px',
                        fontSize: '0.85rem',
                        color: '#64748b',
                      }}>
                        Last checked: {new Date(scheduler.last_check).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                      >
                        {/* Action Buttons */}
                        <div style={{
                          padding: '0 1.5rem 1.5rem 1.5rem',
                          display: 'flex',
                          gap: '0.5rem',
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCheckNow(scheduler.id);
                            }}
                            disabled={checkingScheduler === scheduler.id}
                            style={{
                              ...styles.iconButton,
                              opacity: checkingScheduler === scheduler.id ? 0.7 : 1,
                              cursor: checkingScheduler === scheduler.id ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {checkingScheduler === scheduler.id ? (
                              <>
                                <span style={styles.spinner}></span>
                                Checking...
                              </>
                            ) : (
                              <>
                                <Play size={16} />
                                Check Now
                              </>
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScheduler(scheduler.id);
                            }}
                            style={{ ...styles.iconButton, ...styles.deleteButton }}
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                        </div>

                        {/* Findings Section */}
                        {hasFindings ? (
                          <div style={{
                            background: '#f8fafc',
                            padding: '1.5rem',
                            borderTop: '1px solid #e2e8f0',
                          }}>
                            <div style={{
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              color: '#475569',
                              marginBottom: '1rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                            }}>
                              <Bell size={16} />
                              {schedulerFindings.length} Finding{schedulerFindings.length > 1 ? 's' : ''}
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                              {schedulerFindings.map(finding => (
                                <div
                                  key={finding.id}
                                  style={{
                                    background: '#ffffff',
                                    borderRadius: '12px',
                                    padding: '1.25rem',
                                    border: !finding.is_read ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                  }}
                                >
                                  <div style={styles.findingHeader}>
                                    <div style={{ flex: 1 }}>
                                      <div style={styles.findingTitle}>{finding.title}</div>
                                      <div style={styles.findingMeta}>
                                        {new Date(finding.created_at).toLocaleDateString()}
                                        {!finding.is_read && (
                                          <span style={{
                                            marginLeft: '0.5rem',
                                            color: '#3b82f6',
                                            fontWeight: 600,
                                          }}>
                                            • New
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div style={styles.findingDescription}>
                                    {finding.description}
                                  </div>

                                  <div style={styles.findingActions}>
                                    {finding.source_url && (
                                      <a
                                        href={finding.source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={styles.linkButton}
                                      >
                                        <ExternalLink size={14} />
                                        View Source
                                      </a>
                                    )}
                                    <button
                                      onClick={() => {
                                        setSelectedFinding(finding);
                                        setShowGenerateModal(true);
                                      }}
                                      style={{ ...styles.linkButton, ...styles.generateButton }}
                                    >
                                      <Sparkles size={14} />
                                      Generate Message
                                    </button>
                                    {!finding.is_read && (
                                      <button
                                        onClick={() => handleMarkRead(finding.id)}
                                        style={styles.linkButton}
                                      >
                                        Mark Read
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            background: '#f8fafc',
                            padding: '2rem',
                            textAlign: 'center',
                            color: '#64748b',
                            fontSize: '0.9rem',
                            borderTop: '1px solid #e2e8f0',
                          }}>
                            No findings yet for this contact
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={resetModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={styles.modalTitle}>
                    {modalStep === 1 ? 'Select Contact' : 'Configure Scheduler'}
                  </h2>
                  <p style={styles.modalSubtitle}>
                    {modalStep === 1
                      ? 'Choose a contact to monitor'
                      : `Setting up scheduler for ${selectedContact?.name}`}
                  </p>
                </div>
                <button
                  onClick={resetModal}
                  style={styles.closeButton}
                  onMouseEnter={(e) => e.target.style.background = '#f1f5f9'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  <X size={24} />
                </button>
              </div>

              {modalStep === 1 && (
                <>
                  <div style={styles.contactGrid}>
                    {availableContacts.map(contact => (
                      <div
                        key={contact.id}
                        style={{
                          ...styles.selectableCard,
                          ...(selectedContact?.id === contact.id ? styles.selectableCardSelected : {})
                        }}
                        onClick={() => setSelectedContact(contact)}
                      >
                        <div style={{
                          ...styles.cardCheckbox,
                          ...(selectedContact?.id === contact.id ? styles.cardCheckboxSelected : {})
                        }}>
                          {selectedContact?.id === contact.id && (
                            <Check size={16} color="white" />
                          )}
                        </div>
                        
                        <div style={styles.miniAvatar}>
                          {getInitials(contact.name)}
                        </div>
                        <div style={styles.cardName}>{contact.name}</div>
                        {contact.company && (
                          <div style={styles.cardCompany}>{contact.company}</div>
                        )}
                        {contact.role && (
                          <div style={{ ...styles.cardCompany, marginTop: '0.25rem' }}>
                            {contact.role}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {availableContacts.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '3rem 2rem', color: '#64748b' }}>
                      <p>All contacts already have schedulers</p>
                    </div>
                  )}

                  <div style={styles.modalActions}>
                    <button
                      onClick={resetModal}
                      style={{ ...styles.button, ...styles.cancelButton }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setModalStep(2)}
                      disabled={!selectedContact}
                      style={{
                        ...styles.button,
                        ...styles.nextButton,
                        opacity: !selectedContact ? 0.5 : 1,
                        cursor: !selectedContact ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Next: Configure
                    </button>
                  </div>
                </>
              )}

              {modalStep === 2 && (
                <>
                  <div style={styles.configSection}>
                    <label style={styles.sectionLabel}>Check Frequency</label>
                    <div style={styles.frequencyGrid}>
                      {[
                        { value: 'daily', label: 'Daily', desc: 'Check every day' },
                        { value: 'weekly', label: 'Weekly', desc: 'Check once a week' },
                        { value: 'monthly', label: 'Monthly', desc: 'Check once a month' }
                      ].map(freq => (
                        <div
                          key={freq.value}
                          style={{
                            ...styles.frequencyOption,
                            ...(selectedFrequency === freq.value ? styles.frequencyOptionSelected : {})
                          }}
                          onClick={() => setSelectedFrequency(freq.value)}
                        >
                          <div style={styles.frequencyLabel}>{freq.label}</div>
                          <div style={styles.frequencyDesc}>{freq.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={styles.configSection}>
                    <label style={styles.sectionLabel}>
                      What to Monitor ({selectedAttributes.length} selected)
                    </label>
                    <div style={styles.attributeGrid}>
                      {AVAILABLE_ATTRIBUTES.map(attr => (
                        <div
                          key={attr.id}
                          style={{
                            ...styles.attributeOption,
                            ...(selectedAttributes.includes(attr.id) ? styles.attributeOptionSelected : {})
                          }}
                          onClick={() => toggleAttribute(attr.id)}
                        >
                          <div style={{
                            ...styles.attributeCheckbox,
                            ...(selectedAttributes.includes(attr.id) ? styles.attributeCheckboxSelected : {})
                          }}>
                            {selectedAttributes.includes(attr.id) && (
                              <Check size={14} color="white" />
                            )}
                          </div>
                          <div style={styles.attributeInfo}>
                            <div style={styles.attributeLabel}>{attr.label}</div>
                            <div style={styles.attributeDesc}>{attr.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={styles.modalActions}>
                    <button
                      onClick={() => setModalStep(1)}
                      style={{ ...styles.button, ...styles.backButton }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleAddScheduler}
                      disabled={adding || selectedAttributes.length === 0}
                      style={{
                        ...styles.button,
                        ...styles.submitButton,
                        opacity: adding || selectedAttributes.length === 0 ? 0.5 : 1,
                        cursor: adding || selectedAttributes.length === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {adding ? 'Creating...' : 'Create Scheduler'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      <AnimatePresence>
          {showGenerateModal && selectedFinding && (
            <GenerateMessageModal
              finding={selectedFinding}
              onClose={() => {
                setShowGenerateModal(false);
                setSelectedFinding(null);
              }}
              onComplete={() => {
                setShowGenerateModal(false);
                setSelectedFinding(null);
                loadData(); // Refresh findings
              }}
            />
          )}
        </AnimatePresence>
    </div>
  );
};

export default Scheduler;